import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, afterNextRender, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationStart, PRIMARY_OUTLET, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { ScrollTrigger, gsap } from '../../../motion/gsap';
import { MOTION_CONFIG } from '../../../motion/motion.config';
import { QUERIES } from '../../../motion/motion-env';
import { MotionService } from '../../../motion/motion.service';
import { LANGS, langLink } from '../../../services/i18n-live';

/** Home sections in page order; each link carries `data-section="<id>"`. */
const SECTION_IDS = ['home', 'about', 'services', 'features', 'contact'] as const;
/** Language switcher: short label on the button, full name for assistive tech. */
const LANGUAGES = [
  { code: 'am', short: 'ՀԱՅ', name: 'Հայերեն' },
  { code: 'en', short: 'EN', name: 'English' },
  { code: 'ru', short: 'РУ', name: 'Русский' },
] as const;
const LANG_KEY = 'selectedLanguage';
/** Input that means the user is steering the scroll themselves. */
const USER_SCROLL_INPUTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
/** Latest section-link request, shared by every navbar instance (each page has its own). */
let settleRequest = 0;
/**
 * Language chosen with the keyboard, waiting to receive focus. Shared across instances: a switch
 * to or from Armenian changes the route, so a NEW navbar renders and must take the focus over.
 */
let focusLanguageAfterSwitch: string | null = null;
const SETTLE_ATTEMPTS = 3;

function isLang(value: unknown): value is string {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value);
}

function readStoredLang(): string | null {
  try {
    const lang = localStorage.getItem(LANG_KEY);
    return isLang(lang) ? lang : null;
  } catch {
    return null;
  }
}

function storeLang(lang: string): void {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    // Not critical: the choice just is not remembered.
  }
}

/** Ctrl/Cmd/Shift/Alt or a non-primary button: the browser opens the href in a new tab or window. */
function isModifiedClick(event?: Event): boolean {
  return event instanceof MouseEvent && (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey);
}

function setActive(link: HTMLElement, active: boolean): void {
  link.classList.toggle('is-active', active);
  if (active) link.setAttribute('aria-current', 'true');
  else link.removeAttribute('aria-current');
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  private readonly motion = inject(MotionService);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly destroyRef = inject(DestroyRef);
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private destroyed = false;

  /** Shared `scrollY > 100` signal: flips in the zone only when it changes, never per scroll event. */
  readonly isSticky = this.motion.scrolled;
  isCollapsed = true;
  selectedLanguage = 'am';
  readonly languages = LANGUAGES;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.translate.setDefaultLang('am');

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(() => (this.isCollapsed = true));

    afterNextRender(() => this.motion.afterPaint(() => this.destroyed || this.setupScrollUi()));
    afterNextRender(() => this.focusLanguageButton());
  }

  ngOnInit() {
    // The select mirrors the active language, whoever set it (a page, e.g. the 404 under /en/xyz).
    if (this.translate.currentLang) this.selectedLanguage = this.translate.currentLang;
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => (this.selectedLanguage = event.lang));

    // A language in the URL wins; the stored choice only applies to unprefixed URLs. Loading both
    // would race, and the navbar could end up in a different language than the page.
    const storedLang = this.route.snapshot.params['lang'] ? null : readStoredLang();
    if (storedLang) {
      this.selectedLanguage = storedLang;
      this.translate.use(storedLang);
    }

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const lang = params['lang'];
      if (lang) {
        this.selectedLanguage = lang;
        this.translate.use(lang);
        storeLang(lang);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mm?.revert();
  }

  buildLink(route: string): string {
    return langLink(this.translate, route);
  }

  /** Language-aware `/#id`, `/en#id`, `/ru#id`: works for middle-click, new tabs and without JS. */
  sectionHref(sectionId: string): string {
    return this.motion.sectionHref(sectionId);
  }

  scrollToSection(sectionId: string, event?: Event) {
    // Modified or non-primary clicks open the href in a new tab/window as usual.
    if (isModifiedClick(event)) return;
    this.isCollapsed = true;
    this.settleOnSection(sectionId, this.motion.scrollToSection(sectionId, event));
  }

  /** The logo's target: the home page in the current language ('/', '/en', '/ru'). */
  homeHref(): string {
    return this.motion.homePath();
  }

  /** The logo opens the home page; on the home page it scrolls back to the top, like the Home link. */
  goHome(event: MouseEvent) {
    if (isModifiedClick(event)) return;
    if (this.motion.isHome()) {
      this.scrollToSection('home', event);
      return;
    }
    event.preventDefault();
    this.isCollapsed = true;
    this.router.navigateByUrl(this.motion.homePath());
  }

  changeLanguage(event: Event) {
    this.selectLanguage((event.target as HTMLSelectElement).value);
  }

  /** Arrow keys move between the language buttons, as in a radio group. */
  onLanguageKeydown(event: KeyboardEvent, index: number) {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = this.languages[(index + step + this.languages.length) % this.languages.length];
    // Switching to or from Armenian changes the URL, which tears this page down and renders a
    // new navbar: that instance takes the focus over on its first render (see the constructor).
    // en <-> ru keeps the page, so focus can move right away.
    const routeChanges = (next.code === 'am') !== (this.selectedLanguage === 'am');
    focusLanguageAfterSwitch = next.code;
    this.selectLanguage(next.code);
    if (!routeChanges) this.focusLanguageButton();
  }

  /** Focuses the chosen language's button, if this instance holds the pending keyboard switch. */
  private focusLanguageButton(): void {
    const code = focusLanguageAfterSwitch;
    if (!code || this.selectedLanguage !== code) return;
    const button = this.host.querySelector<HTMLElement>(`.lang-switch button[data-lang="${code}"]`);
    if (!button) return;
    focusLanguageAfterSwitch = null;
    button.focus();
  }

  selectLanguage(lang: string) {
    if (!isLang(lang) || lang === this.selectedLanguage) {
      this.isCollapsed = true;
      return;
    }
    this.selectedLanguage = lang;
    this.isCollapsed = true;

    // Work on the parsed URL so a fragment (`/en#services`) or query (`?mode=practice`) is never
    // mistaken for a path segment. The fragment is dropped: a language switch keeps its scroll position.
    const tree = this.router.parseUrl(this.router.url);
    const segments = (tree.root.children[PRIMARY_OUTLET]?.segments ?? []).map((segment) => segment.path);
    if (isLang(segments[0])) segments.shift();
    if (segments[0] === 'home') segments.length = 0;

    const commands = ['/', ...(lang === 'am' ? [] : [lang]), ...segments];
    const target = this.router.createUrlTree(commands, { queryParams: tree.queryParams });

    // Before navigating: the route change becomes instant (no lane-change slide, no replays).
    this.motion.beginLanguageSwitch();
    this.router.navigateByUrl(target);
    this.translate.use(lang);
    storeLang(lang);
  }

  /**
   * Images above the target that finish loading mid-scroll (and, right after a route change, a
   * smooth-scroll limit still sized for the previous page) can stop the scroll short of the
   * section. A corrective scroll finishes the job, unless the user has taken over meanwhile or a
   * newer section link was clicked (the navbar instance may already be gone after navigating).
   * Scrolls that already land under the bar, or stop at the end of the page, are left alone.
   */
  private settleOnSection(sectionId: string, scrolled: Promise<void>): void {
    if (!this.motion.isBrowser) return;
    const request = ++settleRequest;
    this.motion.outside(() => {
      let interrupted = false;
      const interrupt = () => (interrupted = true);
      const options = { capture: true, passive: true };
      USER_SCROLL_INPUTS.forEach((type) => window.addEventListener(type, interrupt, options));
      const settle = (attempt: number) => {
        const section = document.getElementById(sectionId);
        const miss = section ? section.getBoundingClientRect().top - MOTION_CONFIG.navHeight : 0;
        const atEnd = miss > 0 && window.scrollY >= document.documentElement.scrollHeight - window.innerHeight - 1;
        if (interrupted || request !== settleRequest || Math.abs(miss) <= 2 || atEnd || attempt === SETTLE_ATTEMPTS) {
          USER_SCROLL_INPUTS.forEach((type) => window.removeEventListener(type, interrupt, options));
          return;
        }
        // A numeric target: given an element, Lenis also subtracts its scroll-margin-top. A native
        // smooth scroll can be cut short by a ScrollTrigger refresh, hence the retries.
        this.motion
          .scrollTo(window.scrollY + miss, { duration: 0.5, immediate: attempt === SETTLE_ATTEMPTS - 1 })
          .then(() => settle(attempt + 1));
      };
      scrolled.then(() => settle(0));
    });
  }

  /**
   * Scroll-progress strip and active-section state. Both carry information, so they run under
   * reduced motion too; only CSS decoration differs between the two branches.
   */
  private setupScrollUi(): void {
    const nav = this.host.querySelector<HTMLElement>('nav.navbar');
    const progress = nav?.querySelector<HTMLElement>('.nav-progress');
    if (!nav || !progress) return;

    const mm = (this.mm = gsap.matchMedia(nav));
    mm.add({ motion: QUERIES.motion, reduce: QUERIES.reduce }, () => {
      const writeProgress = (st: ScrollTrigger) => progress.style.setProperty('--p', st.progress.toFixed(4));
      ScrollTrigger.create({ start: 0, end: 'max', onUpdate: writeProgress, onRefresh: writeProgress });

      const links: HTMLElement[] = [];
      if (this.motion.isHome()) {
        const tracked = SECTION_IDS.map((id) => ({
          section: document.getElementById(id),
          link: nav.querySelector<HTMLElement>(`a[data-section="${id}"]`),
        })).filter((item): item is { section: HTMLElement; link: HTMLElement } => !!item.section && !!item.link);

        // Each section stays active until the next tracked one takes over, so the image bands
        // between sections never leave the bar without an active link.
        tracked.forEach(({ section, link }, i) => {
          const next = tracked[i + 1]?.section;
          links.push(link);
          ScrollTrigger.create({
            trigger: section,
            start: 'top 45%',
            ...(next ? { endTrigger: next, end: 'top 45%' } : { end: 'bottom 45%' }),
            onToggle: (st) => setActive(link, st.isActive),
          });
        });
      }

      return () => {
        progress.style.removeProperty('--p');
        links.forEach((link) => setActive(link, false));
      };
    });
  }
}
